import type { Language } from "../config/site";
import type { LegalDocument } from "./shared";

export const privacyDocuments: Record<Language, LegalDocument> = {
  en: {
    eyebrow: "LEGAL",
    title: "Privacy Policy",
    updatedLabel: "Last updated",
    updatedDate: "August 24, 2026",
    introduction: "This policy explains how VaultPin handles information when you use the native iOS game, the Daily Vault service and this website. VaultPin is developed and operated by Miraç Doğan in Türkiye.",
    sections: [
      {
        heading: "1. Controller and contact",
        paragraphs: [
          "Miraç Doğan is responsible for the VaultPin service and the processing described in this policy. Questions or privacy requests can be sent to support@vaultpin.app.",
          "VaultPin does not create a dedicated VaultPin email account or password for you.",
        ],
      },
      {
        heading: "2. Guest identity",
        paragraphs: [
          "If you continue as a guest, the app creates a high-entropy installation UUID and stores it in the iOS Keychain. The raw UUID is sent over an encrypted connection when needed to identify the guest installation, but is not stored in the backend database. The backend stores an HMAC-derived value instead.",
          "This identity lets the service return the same Daily Vault session to the same installation. It is not an advertising identifier, Apple IDFV or hardware identifier.",
        ],
      },
      {
        heading: "3. Game Center identity and alias",
        paragraphs: [
          "If you use Game Center, Apple provides a signed authentication package. The raw teamPlayerID, signature and salt are processed temporarily to verify the request. VaultPin stores an HMAC-derived identity value rather than the raw teamPlayerID.",
          "Your Game Center alias is stored as a display name. It may appear in the Daily Vault leaderboard together with your rank, attempt count, completion time and whether a hint was used. The alias is not used as authentication proof.",
        ],
      },
      {
        heading: "4. Daily Vault gameplay data",
        paragraphs: [
          "The backend associates Daily Vault guesses, attempt sequence, aggregate feedback, elapsed time, hint use, outcome and leaderboard performance with the VaultPin player record. This is necessary to run the shared daily challenge, restore its state across launches, prevent duplicate submissions and calculate rankings.",
          "The encrypted Daily Vault secret is never returned before a terminal result. Secret cryptographic material is removed after its retention period while cycle metadata can remain for retained results.",
        ],
      },
      {
        heading: "5. Information that stays on your device",
        paragraphs: [
          "Practice game progress is not sent to the VaultPin backend. Sound and local notification preferences also remain on the device. The Daily Vault reminder is a local iOS notification, not a remote push notification.",
        ],
      },
      {
        heading: "6. Purchases and StoreKit",
        paragraphs: [
          "Apple handles the one-time StoreKit purchase, restoration and any refund process. VaultPin checks the verified StoreKit entitlement on the device. Receipts and transaction data are not sent to the VaultPin backend.",
          "Deleting VaultPin gameplay data does not remove the Apple-managed purchase entitlement. A purchase can be restored through Apple when available.",
        ],
      },
      {
        heading: "7. Sharing",
        paragraphs: [
          "The system share sheet opens only when you choose to share a result. VaultPin does not automatically post content or disclose the secret code in the prepared share text. Your chosen destination may apply its own privacy terms.",
        ],
      },
      {
        heading: "8. Why information is used",
        paragraphs: ["VaultPin uses the information described above only for app functionality, including:"],
        bullets: [
          "verifying guest or Game Center identity;",
          "creating, restoring and securing Daily Vault sessions;",
          "processing guesses, hints, timing and results;",
          "showing eligible leaderboard entries;",
          "preventing duplicate or abusive requests; and",
          "responding to support and privacy requests.",
        ],
      },
      {
        heading: "9. Service providers and processing locations",
        paragraphs: [
          "The backend and PostgreSQL database run on Railway in the EU West — Amsterdam, Netherlands region. Apple provides Game Center, StoreKit and iOS platform services. This website is intended to be delivered through Cloudflare. Support email is routed through Cloudflare Email Routing to Gmail, which is used to answer the message.",
          "These providers operate their own networks and may process technical connection information under their terms. Data may therefore be processed through service-provider infrastructure outside your country, subject to applicable protections.",
        ],
      },
      {
        heading: "10. No advertising or tracking",
        paragraphs: [
          "VaultPin does not include advertising, analytics, tracking or a third-party crash-reporting SDK. Personal data is not sold or shared for advertising. This website does not include contact forms or third-party scripts; support is provided by email.",
        ],
      },
      {
        heading: "11. Retention",
        paragraphs: ["VaultPin applies the following service retention periods:"],
        bullets: [
          "Daily guess and detailed session data: 30 days;",
          "Daily result and leaderboard data: 13 months;",
          "inactive player records with no retained dependent records: 24 months; and",
          "Daily Vault secret cryptographic material: 30 days after the vault ends.",
        ],
      },
      {
        heading: "12. Deleting your VaultPin data",
        paragraphs: [
          "You can request deletion in the app under Settings → Delete VaultPin Data. After the authenticated deletion succeeds, the player record and associated Daily Vault guesses, sessions, results, leaderboard performance and lifetime statistics are deleted. Shared Daily Vault definitions are not another player's personal data and are preserved.",
          "Deletion does not affect StoreKit purchases, sign you out of the system Game Center account or necessarily cancel local reminder preferences. If you later play the Daily Vault again, a new VaultPin player record may be created.",
        ],
      },
      {
        heading: "13. Security",
        paragraphs: [
          "VaultPin uses measures such as encrypted network transport, Keychain storage on iOS, hashed identity matching and encrypted Daily Vault secrets. No technical system can promise absolute security. Please do not send passwords, tokens, signatures or other sensitive credentials in a support message.",
        ],
      },
      {
        heading: "14. Children",
        paragraphs: [
          "VaultPin is not designed specifically for children. It does not contain gambling, real-money play, cash prizes or games of chance. If you believe a child has provided information in a way that requires action, contact support@vaultpin.app.",
        ],
      },
      {
        heading: "15. Your rights",
        paragraphs: [
          "Depending on where you live, applicable law may give you rights to request access, correction, deletion, restriction, objection or information about processing. These rights can be subject to legal conditions and exceptions. Send a request to support@vaultpin.app so it can be evaluated and answered appropriately.",
        ],
      },
      {
        heading: "16. Changes to this policy",
        paragraphs: [
          "This policy may be updated when VaultPin features, providers or legal requirements change. The updated date will identify the current version. Material changes may also be communicated in the app where appropriate.",
        ],
      },
    ],
  },
  tr: {
    eyebrow: "YASAL",
    title: "Gizlilik Politikası",
    updatedLabel: "Son güncelleme",
    updatedDate: "24 Ağustos 2026",
    introduction: "Bu politika, native iOS oyununu, Günlük Kasa hizmetini ve bu web sitesini kullandığında VaultPin'in bilgileri nasıl ele aldığını açıklar. VaultPin, Türkiye'de Miraç Doğan tarafından geliştirilir ve işletilir.",
    sections: [
      {
        heading: "1. Veri sorumlusu ve iletişim",
        paragraphs: [
          "VaultPin hizmetinden ve bu politikada açıklanan işlemeden Miraç Doğan sorumludur. Sorularını veya gizlilik taleplerini support@vaultpin.app adresine gönderebilirsin.",
          "VaultPin senin için özel bir VaultPin e-posta hesabı veya şifresi oluşturmaz.",
        ],
      },
      {
        heading: "2. Misafir kimliği",
        paragraphs: [
          "Misafir olarak devam edersen uygulama yüksek entropili bir installation UUID oluşturur ve bunu iOS Keychain'de saklar. Ham UUID, misafir kurulumunu tanımlamak gerektiğinde şifreli bağlantı üzerinden gönderilir ancak backend veritabanında saklanmaz. Backend bunun yerine HMAC ile türetilmiş bir değer saklar.",
          "Bu kimlik aynı kuruluma aynı Günlük Kasa oturumunun döndürülmesini sağlar. Reklam kimliği, Apple IDFV veya donanım kimliği değildir.",
        ],
      },
      {
        heading: "3. Game Center kimliği ve alias",
        paragraphs: [
          "Game Center kullanırsan Apple imzalı bir doğrulama paketi sağlar. Ham teamPlayerID, imza ve salt isteği doğrulamak için geçici olarak işlenir. VaultPin, ham teamPlayerID yerine HMAC ile türetilmiş bir kimlik değeri saklar.",
          "Game Center alias'ın görünen ad olarak saklanır. Günlük Kasa sıralamasında sıran, tahmin sayın, bitirme süren ve ipucu kullanıp kullanmadığınla birlikte gösterilebilir. Alias kimlik doğrulama kanıtı olarak kullanılmaz.",
        ],
      },
      {
        heading: "4. Günlük Kasa oyun verileri",
        paragraphs: [
          "Backend; Günlük Kasa tahminlerini, deneme sırasını, toplu geri bildirimi, geçen süreyi, ipucu kullanımını, sonucu ve sıralama performansını VaultPin oyuncu kaydıyla ilişkilendirir. Bu; ortak günlük oyunu yürütmek, uygulama açılışları arasında durumu geri yüklemek, tekrarlanan gönderimleri önlemek ve sıralamayı hesaplamak için gereklidir.",
          "Şifrelenmiş Günlük Kasa sırrı terminal sonuçtan önce geri döndürülmez. Saklama süresi dolduğunda gizli kriptografik materyal kaldırılır; tutulan sonuçlar için cycle metadata'sı korunabilir.",
        ],
      },
      {
        heading: "5. Cihazında kalan bilgiler",
        paragraphs: [
          "Pratik oyun ilerlemesi VaultPin backend'ine gönderilmez. Ses ve yerel bildirim tercihleri de cihazda kalır. Günlük Kasa hatırlatıcısı remote push değil, yerel bir iOS bildirimidir.",
        ],
      },
      {
        heading: "6. Satın alımlar ve StoreKit",
        paragraphs: [
          "Tek seferlik StoreKit satın alımını, geri yüklemeyi ve olası iade sürecini Apple yönetir. VaultPin doğrulanmış StoreKit entitlement'ını cihazda kontrol eder. Receipt ve transaction verileri VaultPin backend'ine gönderilmez.",
          "VaultPin oyun verilerini silmek Apple tarafından yönetilen satın alma hakkını kaldırmaz. Uygun olduğunda satın alım Apple üzerinden geri yüklenebilir.",
        ],
      },
      {
        heading: "7. Paylaşım",
        paragraphs: [
          "Sistem paylaşım ekranı yalnız sonucu paylaşmayı seçtiğinde açılır. VaultPin otomatik paylaşım yapmaz ve hazırlanan paylaşım metninde gizli kodu açıklamaz. Seçtiğin hedefin kendi gizlilik koşulları geçerli olabilir.",
        ],
      },
      {
        heading: "8. Bilgilerin kullanım amaçları",
        paragraphs: ["VaultPin yukarıdaki bilgileri yalnız uygulama işlevleri için kullanır:"],
        bullets: [
          "misafir veya Game Center kimliğini doğrulamak;",
          "Günlük Kasa oturumlarını oluşturmak, geri yüklemek ve güvenli tutmak;",
          "tahmin, ipucu, süre ve sonuçları işlemek;",
          "uygun sıralama kayıtlarını göstermek;",
          "tekrarlanan veya kötüye kullanılan istekleri önlemek; ve",
          "destek ve gizlilik taleplerini yanıtlamak.",
        ],
      },
      {
        heading: "9. Hizmet sağlayıcılar ve işleme yerleri",
        paragraphs: [
          "Backend ve PostgreSQL veritabanı Railway'in EU West — Amsterdam, Hollanda bölgesinde çalışır. Apple; Game Center, StoreKit ve iOS platform hizmetlerini sağlar. Bu web sitesinin Cloudflare üzerinden yayınlanması planlanır. Destek e-postaları Cloudflare Email Routing ile mesajı yanıtlamak için kullanılan Gmail'e yönlendirilir.",
          "Bu sağlayıcılar kendi ağlarını işletir ve teknik bağlantı bilgilerini kendi koşulları kapsamında işleyebilir. Bu nedenle veriler, geçerli korumalara tabi olarak ülken dışındaki hizmet sağlayıcı altyapılarında işlenebilir.",
        ],
      },
      {
        heading: "10. Reklam veya tracking yoktur",
        paragraphs: [
          "VaultPin reklam, analytics, tracking veya üçüncü taraf crash-reporting SDK'sı içermez. Kişisel veriler satılmaz veya reklam amacıyla paylaşılmaz. Bu web sitesinde iletişim formu veya üçüncü taraf script bulunmaz; destek e-posta ile sağlanır.",
        ],
      },
      {
        heading: "11. Saklama süreleri",
        paragraphs: ["VaultPin aşağıdaki hizmet saklama sürelerini uygular:"],
        bullets: [
          "Günlük tahmin ve ayrıntılı session verileri: 30 gün;",
          "Günlük result ve leaderboard verileri: 13 ay;",
          "saklanan bağlı kaydı bulunmayan inactive Player kayıtları: 24 ay; ve",
          "Günlük Kasa gizli kriptografik materyali: kasa bittikten sonra 30 gün.",
        ],
      },
      {
        heading: "12. VaultPin verilerini silme",
        paragraphs: [
          "Uygulamada Ayarlar → VaultPin Verilerini Sil yolunu kullanarak silme talebinde bulunabilirsin. Kimliği doğrulanmış silme başarıyla tamamlandıktan sonra oyuncu kaydı ile ilişkili Günlük Kasa tahminleri, session'ları, sonuçları, sıralama performansı ve lifetime istatistikleri silinir. Paylaşılan Günlük Kasa tanımları başka bir oyuncunun kişisel verisi değildir ve korunur.",
          "Silme işlemi StoreKit satın alımlarını etkilemez, sistem Game Center hesabından çıkış yapmaz ve yerel hatırlatıcı tercihlerini zorunlu olarak iptal etmez. Daha sonra yeniden Günlük Kasa oynarsan yeni bir VaultPin oyuncu kaydı oluşturulabilir.",
        ],
      },
      {
        heading: "13. Güvenlik",
        paragraphs: [
          "VaultPin şifreli ağ aktarımı, iOS Keychain saklama, hash tabanlı kimlik eşleştirme ve şifrelenmiş Günlük Kasa sırları gibi önlemler kullanır. Hiçbir teknik sistem mutlak güvenlik sözü veremez. Destek mesajında parola, token, imza veya başka hassas kimlik bilgileri gönderme.",
        ],
      },
      {
        heading: "14. Çocukların gizliliği",
        paragraphs: [
          "VaultPin çocuklara özel tasarlanmamıştır. Bahis, gerçek para oyunu, nakit ödül veya şans oyunu içermez. Bir çocuğun işlem gerektiren biçimde bilgi verdiğini düşünüyorsan support@vaultpin.app adresine ulaş.",
        ],
      },
      {
        heading: "15. Hakların",
        paragraphs: [
          "Yaşadığın yere bağlı olarak geçerli hukuk; erişim, düzeltme, silme, kısıtlama, itiraz veya işleme hakkında bilgi isteme hakları tanıyabilir. Bu haklar yasal şartlara ve istisnalara tabi olabilir. Talebinin uygun şekilde değerlendirilmesi ve yanıtlanması için support@vaultpin.app adresine gönder.",
        ],
      },
      {
        heading: "16. Bu politikadaki değişiklikler",
        paragraphs: [
          "VaultPin özellikleri, sağlayıcıları veya hukuki gereklilikler değiştiğinde bu politika güncellenebilir. Son güncelleme tarihi geçerli sürümü gösterir. Önemli değişiklikler uygun olduğunda uygulama içinde de bildirilebilir.",
        ],
      },
    ],
  },
};
